import { useState, useEffect } from 'react';
import { Plus, FileText, Edit, X, ChevronRight, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { useConfirmation } from '../../context/ConfirmationContext';
import { useNavigate, useLocation } from 'react-router-dom';

export default function LecturerExams() {
  const { showConfirmation } = useConfirmation();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMarksModal, setShowMarksModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [editingExamId, setEditingExamId] = useState(null);
  const [examStudents, setExamStudents] = useState([]);
  const [examComponents, setExamComponents] = useState([]);
  const [examStats, setExamStats] = useState({ highest: null, lowest: null });

  const [formData, setFormData] = useState({
    exam_name: '', subject_id: '', grade_id: '', exam_date: '', description: '',
    components: [{ component_name: '', max_marks: '' }]
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadData(); }, []);

  // If navigated here with edit state (from Exam Detail), open edit modal
  const location = useLocation();
  useEffect(() => {
    const editId = location.state?.editExamId;
    if (editId) {
      // If exams already loaded, open modal with that exam
      const found = exams.find(e => String(e.exam_id) === String(editId));
      if (found) {
        openEditModal(found);
        // clear the navigation state so we don't reopen
        navigate('/lecturer/exams', { replace: true });
      } else {
        // Exams may not be loaded yet; set editing id and open modal; it will be filled when exams arrive
        setEditingExamId(editId);
        setShowCreateModal(true);
        navigate('/lecturer/exams', { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, exams]);

  // If we set editingExamId earlier while exams were not yet loaded, finalize now
  useEffect(() => {
    if (editingExamId && showCreateModal && exams.length) {
      const found = exams.find(e => String(e.exam_id) === String(editingExamId));
      if (found) {
        openEditModal(found);
      }
    }
  }, [editingExamId, exams, showCreateModal]);

  const loadData = async () => {
    try {
      const [examsRes, dashRes] = await Promise.all([
        api.get('/lecturer/exams'),
        api.get('/lecturer/dashboard')
      ]);

      if (examsRes.data.success && examsRes.data.data) {
        const examsData = examsRes.data.data.exams || [];
        setExams(examsData);
      }
      
      if (dashRes.data.success && dashRes.data.data && dashRes.data.data.dashboard) {
        const subjectsData = dashRes.data.data.dashboard.subjects || [];
        setSubjects(subjectsData);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.exam_name.trim()) {
      newErrors.exam_name = 'Exam name is required';
    }
    if (!formData.subject_id || formData.subject_id === '' || formData.subject_id === '0') {
      newErrors.subject_id = 'Please select a subject';
    }
    if (!formData.grade_id || formData.grade_id === '' || formData.grade_id === '0') {
      newErrors.grade_id = 'Grade must be selected';
    }
    if (formData.components.length === 0 || formData.components.every(c => !c.component_name)) {
      newErrors.components = 'Please add at least one component';
    }
    
    formData.components.forEach((c, i) => {
      if (c.component_name && !c.max_marks) {
        newErrors[`component_${i}_marks`] = 'Max marks required';
      }
      if (c.max_marks && parseInt(c.max_marks) < 1) {
        newErrors[`component_${i}_marks`] = 'Must be at least 1';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addComponent = () => {
    setFormData({ ...formData, components: [...formData.components, { component_name: '', max_marks: '' }] });
  };

  const removeComponent = (index) => {
    const newComponents = formData.components.filter((_, i) => i !== index);
    setFormData({ ...formData, components: newComponents });
  };

  const updateComponent = (index, field, value) => {
    const newComponents = [...formData.components];
    newComponents[index][field] = value;
    setFormData({ ...formData, components: newComponents });
  };

  const handleSubjectChange = (subjectId) => {
    const numSubjectId = parseInt(subjectId);
    const subject = subjects.find(s => s.subject_id === numSubjectId);
    if (subject) {
      setFormData({ ...formData, subject_id: numSubjectId, grade_id: subject.grade_id });
      setErrors({ ...errors, subject_id: '', grade_id: '' });
    } else {
      console.warn('⚠️ Subject not found!');
      setFormData({ ...formData, subject_id: numSubjectId, grade_id: '' });
    }
  };

  const openEditModal = (exam) => {
    setEditingExamId(exam.exam_id);
    setFormData({
      exam_name: exam.exam_name,
      subject_id: exam.subject_id,
      grade_id: exam.grade_id,
      exam_date: exam.exam_date ? exam.exam_date.split('T')[0] : '',
      description: exam.description || '',
      components: [{ component_name: '', max_marks: '' }]
    });
    setShowCreateModal(true);
    setErrors({});
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingExamId(null);
    setFormData({
      exam_name: '', subject_id: '', grade_id: '', exam_date: '', description: '',
      components: [{ component_name: '', max_marks: '' }]
    });
    setErrors({});
    setError('');
  };

  const handleCreateOrUpdateExam = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        exam_name: formData.exam_name.trim(),
        subject_id: parseInt(formData.subject_id),
        grade_id: parseInt(formData.grade_id),
        exam_date: formData.exam_date || null,
        description: formData.description || null,
        components: formData.components.filter(c => c.component_name && c.max_marks).map(c => ({
          component_name: c.component_name.trim(),
          max_marks: parseInt(c.max_marks)
        })),
        // Calculate total_marks from components
        total_marks: formData.components
          .filter(c => c.component_name && c.max_marks)
          .reduce((sum, c) => sum + parseInt(c.max_marks), 0)
      };

      if (editingExamId) {
        const res = await api.put(`/lecturer/exams/${editingExamId}`, payload);
        if (res.data.success) {
          setSuccess('Exam updated successfully!');
          closeModal();
          loadData();
        }
      } else {
        const res = await api.post('/lecturer/exams', payload);
        if (res.data.success) {
          setSuccess('Exam created successfully!');
          closeModal();
          loadData();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save exam');
    }
  };

  const handleDeleteExam = async (exam) => {
    showConfirmation({
      title: 'Delete Exam?',
      message: `Are you sure you want to delete "${exam.exam_name}"?\n\nThis action cannot be undone. All associated marks and data will be permanently deleted.`,
      confirmText: 'Delete Exam',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await api.delete(`/lecturer/exams/${exam.exam_id}`);
          if (res.data.success) {
            setSuccess('Exam deleted successfully!');
            loadData();
          }
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to delete exam');
        }
      }
    });
  };

  const openMarksModal = async (exam) => {
    setSelectedExam(exam);
    try {
      const res = await api.get(`/lecturer/exams/${exam.exam_id}/marks`);
      if (res.data.success) {
        const students = res.data.data.students || [];
        const components = res.data.data.components || [];
        setExamStudents(students);
        setExamComponents(components);

        // Compute highest and lowest totals (treat null/empty as 0)
        const totals = students.map(s => parseFloat(s.total_marks) || 0);
        const highest = totals.length > 0 ? Math.max(...totals) : null;
        const lowest = totals.length > 0 ? Math.min(...totals) : null;
        setExamStats({ highest, lowest });
      }
    } catch (err) {
    }
    setShowMarksModal(true);
  };

  // viewExamDetails removed — rows now navigate to the full exam detail page (/lecturer/exams/:examId).

  const saveStudentMarks = async (studentId, componentMarks) => {
    try {
      const components = componentMarks.map(cm => ({
        componentId: cm.component_id,
        marksObtained: cm.marks_obtained === '' || cm.marks_obtained === null ? 0 : Math.round(Number(cm.marks_obtained) * 100) / 100
      }));
      
      const totalMarks = components.reduce((sum, c) => sum + c.marksObtained, 0);

      const res = await api.post(`/lecturer/exams/${selectedExam.exam_id}/marks`, {
        marks: [{
          studentId: studentId,
          totalMarks: totalMarks,
          components: components
        }]
      });

      setSuccess('Marks saved successfully!');
      const refreshed = await api.get(`/lecturer/exams/${selectedExam.exam_id}/marks`);
      if (refreshed.data.success) setExamStudents(refreshed.data.data.students || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save marks');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Exams & Marks</h1>
          <p className="text-gray-500">Create exams and enter student marks</p>
        </div>
        <button onClick={() => { setEditingExamId(null); setShowCreateModal(true); setErrors({}); }} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
          <Plus className="w-5 h-5" /> Create Exam
        </button>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Your Exams ({exams.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Exam</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Total Marks</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {exams.map((exam) => (
                  <tr key={exam.exam_id} className={`hover:bg-gray-50 cursor-pointer`} onClick={() => navigate(`/lecturer/exams/${exam.exam_id}`)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg"><FileText className="w-4 h-4 text-purple-600" /></div>
                        <span className="font-medium text-gray-800">{exam.exam_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{exam.subject_name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs">{exam.grade_name}</span></td>
                    <td className="px-4 py-3 text-gray-600">{exam.total_marks}</td>
                    <td className="px-4 py-3 text-gray-600">{exam.exam_date ? new Date(exam.exam_date).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openMarksModal(exam)} className="flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition text-sm">
                        Marks <ChevronRight className="w-4 h-4" />
                      </button>
                      <button onClick={() => openEditModal(exam)} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteExam(exam)} className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {exams.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No exams created yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Exam Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">{editingExamId ? 'Edit Exam' : 'Create New Exam'}</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <form onSubmit={handleCreateOrUpdateExam} className="space-y-5">
              {/* Exam Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exam Name *</label>
                <input
                  type="text"
                  value={formData.exam_name}
                  onChange={(e) => { setFormData({...formData, exam_name: e.target.value}); if(errors.exam_name) setErrors({...errors, exam_name: ''}); }}
                  placeholder="Enter exam name"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition ${errors.exam_name ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-indigo-500'}`}
                />
                {errors.exam_name && <p className="mt-1 text-sm text-red-600">{errors.exam_name}</p>}
              </div>

              {/* Grade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grade *</label>
                <select
                  value={formData.grade_id}
                  onChange={(e) => {
                    const gid = e.target.value ? parseInt(e.target.value) : '';
                    // Clear subject when grade changes so user picks subject from the selected grade
                    setFormData({ ...formData, grade_id: gid, subject_id: '' });
                    setErrors({ ...errors, grade_id: '', subject_id: '' });
                  }}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition ${errors.grade_id ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-indigo-500'}`}
                >
                  <option value="">Select a grade</option>
                  {/* Build unique grade list from subjects */}
                  {Array.from(new Map(subjects.map(s => [s.grade_id, { grade_id: s.grade_id, grade_name: s.grade_name }])).values()).map(g => (
                    <option key={g.grade_id} value={g.grade_id}>{g.grade_name}</option>
                  ))}
                </select>
                {errors.grade_id && <p className="mt-1 text-sm text-red-600">{errors.grade_id}</p>}
              </div>

              {/* Subject (filtered by selected grade) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                <select
                  value={formData.subject_id}
                  onChange={(e) => {
                    const sid = e.target.value ? parseInt(e.target.value) : '';
                    // Keep the lecturer-selected grade; only update subject.
                    setFormData({ ...formData, subject_id: sid });
                    setErrors({ ...errors, subject_id: '' });
                  }}
                  disabled={!formData.grade_id}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition ${errors.subject_id ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-indigo-500'}`}
                >
                  {!formData.grade_id && <option value="">Select a grade first</option>}
                  {formData.grade_id && (
                    <>
                      <option value="">Select a subject</option>
                      {subjects.filter(s => s.grade_id === parseInt(formData.grade_id)).map(s => (
                        <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
                      ))}
                    </>
                  )}
                </select>
                {errors.subject_id && <p className="mt-1 text-sm text-red-600">{errors.subject_id}</p>}
              </div>

              {/* Exam Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exam Date</label>
                <input
                  type="date"
                  value={formData.exam_date}
                  onChange={(e) => setFormData({...formData, exam_date: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter exam description"
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              {/* Components */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Exam Components *</label>
                  <div className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold">
                    Total: {formData.components.filter(c => c.component_name && c.max_marks).reduce((sum, c) => sum + (parseInt(c.max_marks) || 0), 0)} marks
                  </div>
                </div>
                {errors.components && <p className="mb-2 text-sm text-red-600">{errors.components}</p>}
                <div className="space-y-3">
                  {formData.components.map((comp, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={comp.component_name}
                          onChange={(e) => updateComponent(idx, 'component_name', e.target.value)}
                          placeholder="Component name (e.g., Theory, Practical)"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition ${errors[`component_${idx}_marks`] ? 'border-red-500' : 'border-gray-300 focus:border-indigo-500'}`}
                        />
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          value={comp.max_marks}
                          onChange={(e) => updateComponent(idx, 'max_marks', e.target.value)}
                          placeholder="Max marks"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none transition ${errors[`component_${idx}_marks`] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-indigo-500'}`}
                          min="1"
                        />
                      </div>
                      {formData.components.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeComponent(idx)}
                          className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {formData.components.map((_, idx) => errors[`component_${idx}_marks`] && (
                    <p key={`err-${idx}`} className="text-sm text-red-600">{errors[`component_${idx}_marks`]}</p>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addComponent}
                  className="mt-3 px-4 py-2 border-2 border-dashed border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 transition font-medium"
                >
                  + Add Component
                </button>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium">
                  {editingExamId ? 'Update Exam' : 'Create Exam'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Marks Modal */}
      {showMarksModal && selectedExam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMarksModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Enter Marks: {selectedExam.exam_name}</h2>
              <button onClick={() => setShowMarksModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Student</th>
                    {examComponents.map((comp) => (
                      <th key={comp.component_id} className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">
                        {comp.component_name}
                        <div className="text-xs text-gray-400">(Max: {comp.max_marks})</div>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Total</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {examStudents.map((student) => {
                    const studentMarks = student.component_marks || [];
                    const total = studentMarks.reduce((sum, m) => sum + (parseFloat(m.marks_obtained) || 0), 0);
                    return (
                      <tr key={student.student_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{student.full_name}</td>
                        {examComponents.map((comp) => {
                          const mark = studentMarks.find(m => m.component_id === comp.component_id);
                          const marksValue = mark?.marks_obtained || '';
                          return (
                            <td key={comp.component_id} className="px-4 py-3">
                              <input
                                type="number"
                                value={marksValue}
                                onChange={(e) => {
                                  const newValue = parseFloat(e.target.value) || 0;
                                  if (newValue > comp.max_marks) {
                                    setError(`Cannot enter more than ${comp.max_marks} for ${comp.component_name}`);
                                    setTimeout(() => setError(''), 3000);
                                    return;
                                  }
                                  if (!mark) {
                                    studentMarks.push({ component_id: comp.component_id, marks_obtained: newValue });
                                  } else {
                                    mark.marks_obtained = newValue;
                                  }
                                  setExamStudents([...examStudents]);
                                  setError('');
                                }}
                                placeholder="0"
                                min="0"
                                max={comp.max_marks}
                                className="w-16 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-indigo-500"
                              />
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 font-medium text-gray-800">{total}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => saveStudentMarks(student.student_id, studentMarks)}
                            className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100 transition text-sm font-medium"
                          >
                            Save
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {examStudents.length === 0 && (
                    <tr><td colSpan={examComponents.length + 3} className="px-4 py-8 text-center text-gray-400">No students enrolled in this grade</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowMarksModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
